import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Instagram, Facebook, Globe, Gift } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function FeedbackThanks() {
  const { outletId } = useParams();
  const [landing, setLanding] = useState(null);

  useEffect(() => {
    axios.get(`${API}/feedback/landing/${outletId}`)
      .then(r => setLanding(r.data))
      .catch(() => setLanding({ _missing: true }));
  }, [outletId]);

  if (!landing) return <div className="min-h-screen flex items-center justify-center bg-[#F9F9F8]">Loading…</div>;
  if (landing._missing) return <div className="min-h-screen flex items-center justify-center bg-[#F9F9F8] p-6 text-center"><p className="text-sm text-[#5C6B64]">Outlet not found.</p></div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F0F7F2] to-[#F9F9F8] px-5 py-10 flex flex-col items-center" data-testid="feedback-thanks-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          {landing.has_logo && (
            <img src={`${API}/outlets/${outletId}/logo`} alt={landing.outlet_name}
              className="h-20 w-auto mx-auto mb-3 object-contain" />
          )}
          <div className="w-16 h-16 bg-[#2D6A4F] rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
            <CheckCircle className="w-9 h-9 text-white" strokeWidth={2.5} />
          </div>
          <h1 className="text-2xl font-bold text-[#1A2421]" data-testid="thanks-title">{landing.thank_you_title}</h1>
          <p className="text-sm text-[#5C6B64] mt-2 leading-relaxed" data-testid="thanks-message">{landing.thank_you_message}</p>
          <p className="text-xs font-semibold text-[color:var(--color-primary)] mt-4 uppercase tracking-wider">{landing.outlet_name}</p>
        </div>

        {landing.offer_banner_b64 && (
          <div className="rounded-2xl overflow-hidden mb-4 shadow-sm border border-[#E2E8DF]" data-testid="offer-banner">
            <img src={landing.offer_banner_b64} alt="Offer" className="w-full h-auto object-cover" />
          </div>
        )}

        {landing.offer_text && (
          <div className="bg-white border border-amber-200 rounded-2xl p-5 mb-4 shadow-sm" data-testid="offer-text">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
                <Gift className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-amber-700 mb-1">Special offer</p>
                <p className="text-sm text-[#1A2421] leading-relaxed whitespace-pre-wrap">{landing.offer_text}</p>
              </div>
            </div>
          </div>
        )}

        {(landing.social_instagram || landing.social_facebook || landing.social_website) && (
          <div className="flex items-center justify-center gap-3 mb-6" data-testid="social-links">
            {landing.social_instagram && (
              <a href={landing.social_instagram} target="_blank" rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-white border border-[#E2E8DF] flex items-center justify-center hover:bg-[#F3F5F2] transition-colors">
                <Instagram className="w-5 h-5 text-[#C13584]" />
              </a>
            )}
            {landing.social_facebook && (
              <a href={landing.social_facebook} target="_blank" rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-white border border-[#E2E8DF] flex items-center justify-center hover:bg-[#F3F5F2] transition-colors">
                <Facebook className="w-5 h-5 text-[#1877F2]" />
              </a>
            )}
            {landing.social_website && (
              <a href={landing.social_website} target="_blank" rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-white border border-[#E2E8DF] flex items-center justify-center hover:bg-[#F3F5F2] transition-colors">
                <Globe className="w-5 h-5 text-[color:var(--color-primary)]" />
              </a>
            )}
          </div>
        )}

        <div className="text-center">
          <Link to={`/feedback/${outletId}/dinein`}
            className="text-xs text-[#5C6B64] underline hover:text-[color:var(--color-primary)]">
            Submit another feedback
          </Link>
        </div>
      </div>
    </div>
  );
}
